import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { loginUser, registerUser } from '../api/auth';
import Button from '../components/Button';
import styles from './Login.module.css';

// Режимы формы
const MODE_LOGIN = 'login';
const MODE_REGISTER = 'register';

const REDEEM_TOKEN_KEY = 'pendingRedeemToken';

// Начальное состояние полей формы
const INITIAL_FORM = { email: '', password: '', confirmPassword: '', telegram_username: '' };

const Login = () => {
  const [mode, setMode] = useState(MODE_LOGIN);
  const [form, setForm] = useState(INITIAL_FORM);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const isRegister = mode === MODE_REGISTER;

  const handleChange = (e) => {
    const { name, value } = e.target;
    const cleanValue = name === 'telegram_username' ? value.replace(/^@/, '') : value;
    setForm((prev) => ({ ...prev, [name]: cleanValue }));
    // Сбрасываем ошибку при изменении поля
    if (error) setError('');
  };

  const switchMode = () => {
    setMode((prev) => (prev === MODE_LOGIN ? MODE_REGISTER : MODE_LOGIN));
    setForm(INITIAL_FORM);
    setError('');
  };

  // Клиентская валидация формы
  const validate = () => {
    if (!form.email.includes('@')) {
      return 'Введите корректный email-адрес';
    }
    if (form.password.length < 6) {
      return 'Пароль должен содержать не менее 6 символов';
    }
    if (isRegister && form.password !== form.confirmPassword) {
      return 'Пароли не совпадают';
    }
    if (isRegister) {
      const nick = form.telegram_username.replace(/^@/, '').trim();
      if (!nick) {
        return 'Telegram-ник обязателен';
      }
      if (!/^[a-zA-Z0-9_]{5,32}$/.test(nick)) {
        return 'Telegram-ник должен содержать 5–32 символа: буквы, цифры, _';
      }
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      let data;
      if (isRegister) {
        const tg = `@${form.telegram_username.trim()}`;
        data = await registerUser(form.email, form.password, tg);
      } else {
        data = await loginUser(form.email, form.password);
      }

      // Сохраняем токен + данные пользователя и перенаправляем
      login(data.token, data.user);

      // Если есть отложенная активация ссылки — возвращаем пользователя на /redeem
      const pendingRedeem = sessionStorage.getItem(REDEEM_TOKEN_KEY);
      if (pendingRedeem) {
        sessionStorage.removeItem(REDEEM_TOKEN_KEY);
        navigate(`/redeem/${pendingRedeem}`);
        return;
      }

      navigate(data.user.telegram_username ? '/news' : '/profile');
    } catch (err) {
      // Показываем сообщение об ошибке из ответа сервера или общее сообщение
      const message =
        err.response?.data?.message || 'Произошла ошибка. Попробуйте ещё раз.';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        {/* Логотип */}
        <Link to="/" className={styles.logo}>
          <span className={styles.logoAccent}>Терушкин</span> ОС
        </Link>

        {/* Заголовок */}
        <h1 className={styles.title}>{isRegister ? 'Регистрация' : 'Вход в аккаунт'}</h1>
        <p className={styles.subtitle}>
          {isRegister
            ? 'Создайте аккаунт, чтобы вступить в клуб'
            : 'Войдите, чтобы попасть в личный кабинет'}
        </p>

        {/* Форма */}
        <form onSubmit={handleSubmit} className={styles.form} noValidate>
          <div className={styles.field}>
            <label htmlFor="email" className={styles.label}>
              Email
            </label>
            <input
              id="email"
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              className={styles.input}
              placeholder="example@mail.ru"
              autoComplete="email"
              required
            />
          </div>

          <div className={styles.field}>
            <label htmlFor="password" className={styles.label}>
              Пароль
            </label>
            <input
              id="password"
              type="password"
              name="password"
              value={form.password}
              onChange={handleChange}
              className={styles.input}
              placeholder="Минимум 6 символов"
              autoComplete={isRegister ? 'new-password' : 'current-password'}
              required
            />
          </div>

          {isRegister && (
            <div className={styles.field}>
              <label htmlFor="confirmPassword" className={styles.label}>
                Подтверждение пароля
              </label>
              <input
                id="confirmPassword"
                type="password"
                name="confirmPassword"
                value={form.confirmPassword}
                onChange={handleChange}
                className={styles.input}
                placeholder="Повторите пароль"
                autoComplete="new-password"
                required
              />
            </div>
          )}

          {isRegister && (
            <div className={styles.field}>
              <label htmlFor="telegram_username" className={styles.label}>
                Telegram-ник
              </label>
              <input
                id="telegram_username"
                type="text"
                name="telegram_username"
                value={form.telegram_username}
                onChange={handleChange}
                className={styles.input}
                placeholder="@username"
                autoComplete="off"
              />
            </div>
          )}

          {/* Сообщение об ошибке */}
          {error && <p className={styles.error}>{error}</p>}

          <Button
            type="submit"
            variant="primary"
            size="md"
            className={styles.submitButton}
            disabled={isLoading}
          >
            {isLoading
              ? 'Пожалуйста, подождите...'
              : isRegister
              ? 'Зарегистрироваться'
              : 'Войти'}
          </Button>
        </form>

        {/* Переключатель режима */}
        <p className={styles.switchText}>
          {isRegister ? 'Уже есть аккаунт?' : 'Нет аккаунта?'}
          {' '}
          <button type="button" onClick={switchMode} className={styles.switchLink}>
            {isRegister ? 'Войдите' : 'Зарегистрируйтесь'}
          </button>
        </p>
      </div>
    </div>
  );
};

export default Login;
